// Tier 2: Zod parse de TODA resposta de /api/charges.
// Schemas de request vivem em @control-center/shared (compartilhado com API).

import {
  chargeDetailResponseSchema,
  chargeListItemSchema,
  createManualChargeResponseSchema,
  listChargesResponseSchema,
  markPaidManualResponseSchema,
  updateDueDateResponseSchema,
  type ChargeDetail,
  type CreateManualChargeRequest,
  type ChargeListItem,
  type ListChargesQuery,
  type ListChargesResponse,
  type MarkPaidManualRequest,
  type MarkPaidManualResponse,
  type UpdateDueDateRequest,
  type UpdateDueDateResponse,
} from '@control-center/shared';

import { apiClient } from './api-client';

function buildQueryString(query: ListChargesQuery): string {
  const params = new URLSearchParams();
  params.set('scope', query.scope);
  params.set('page', String(query.page));
  params.set('pageSize', String(query.pageSize));
  // Filtros opcionais (story 4.10)
  if (query.status) params.set('status', query.status);
  if (query.dueFrom) params.set('dueFrom', query.dueFrom);
  if (query.dueTo) params.set('dueTo', query.dueTo);
  if (query.clientId) params.set('clientId', query.clientId);
  if (query.subscriptionId) params.set('subscriptionId', query.subscriptionId);
  return params.toString();
}

export async function fetchCharges(query: ListChargesQuery): Promise<ListChargesResponse> {
  const res = await apiClient.get<unknown>(`/api/charges?${buildQueryString(query)}`);
  return listChargesResponseSchema.parse(res.data);
}

export async function fetchChargeDetail(id: string): Promise<ChargeDetail> {
  const res = await apiClient.get<unknown>(`/api/charges/${id}`);
  const parsed = chargeDetailResponseSchema.parse(res.data);
  return parsed.data;
}

export async function createManualCharge(
  payload: CreateManualChargeRequest,
): Promise<ChargeListItem> {
  const res = await apiClient.post<unknown>('/api/charges', payload);
  const parsed = createManualChargeResponseSchema.parse(res.data);
  return chargeListItemSchema.parse(parsed.data);
}

export async function markPaidManual(
  chargeId: string,
  payload: MarkPaidManualRequest,
): Promise<MarkPaidManualResponse['data']> {
  const res = await apiClient.post<unknown>(`/api/charges/${chargeId}/mark-paid-manual`, payload);
  const parsed = markPaidManualResponseSchema.parse(res.data);
  return parsed.data;
}

export async function updateChargeDueDate(
  chargeId: string,
  payload: UpdateDueDateRequest,
): Promise<UpdateDueDateResponse['data']> {
  const res = await apiClient.post<unknown>(`/api/charges/${chargeId}/update-due-date`, payload);
  const parsed = updateDueDateResponseSchema.parse(res.data);
  return parsed.data;
}

export interface RetryFailedChargeResult {
  chargeId: string;
  status: string;
  attemptsCount: number;
  asaasPaymentId: string | null;
  paymentLink: string | null;
}

export async function retryFailedCharge(chargeId: string): Promise<RetryFailedChargeResult> {
  const res = await apiClient.post<{ data: RetryFailedChargeResult }>(
    `/api/charges/${chargeId}/retry`,
  );
  return res.data.data;
}

export interface AcceptChargebackLossResult {
  chargeId: string;
  status: string;
  subscriptionCancelled: boolean;
  divergenceResolved: boolean;
}

/** Story 4.12 — operador aceita perda do chargeback. */
export async function acceptChargebackLoss(chargeId: string): Promise<AcceptChargebackLossResult> {
  const res = await apiClient.post<{ data: AcceptChargebackLossResult }>(
    `/api/charges/${chargeId}/accept-chargeback-loss`,
  );
  return res.data.data;
}
